import { useEffect, useMemo, useState } from 'react';
import { UserPlus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePartnerSearch, type PartnerSearchResult } from '@/api/extensions/usePartnerSearch';
import { useAdminCreateRegistration } from '@/api/extensions/useRegistrationOps';
import { useUserBuddyGroupForRodeo } from '@/api/extensions/useBuddyGroupOps';
import { useDebounce } from '@/hooks/useDebounce';
import { useEvents } from '@/api/generated_hooks/generated-events-hooks';
import { useRodeos } from '@/api/generated_hooks/generated-rodeos-hooks';
import { isTeamRopingEvent } from '@/utils/eventTypes';
import { httpClient } from '@/api/http-client';
import { formatInTimezone } from '@/utils/timezone';

// Mirror EditPreferencesDialog's option shape so the values round-trip
// identically: stored prefs may carry a [isoDatetime] disambiguation
// suffix for same-named perfs, and the backend matches by that exact
// string. Keep the two dialogs in lockstep.
interface PerfChoice {
  value: string;
  label: string;
  isSlack: boolean;
}
const DRAW_OUT_VALUE = 'Draw Out';
const NONE_VALUE = '__none__';

interface AdminAddContestantDialogProps {
  eventId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Position = 'Header' | 'Heeler';
type PaymentMethod = 'pay_later' | 'cash' | 'stripe';
// Team-roping partner-mode toggle. Defaults to 'with_partner' so existing
// behaviour is preserved; 'individual' is the new admin escape hatch that
// holds a partnerless seat for later pairing on the Draw Management page.
type PartnerMode = 'with_partner' | 'individual';

// The picker `usePartnerSearch` is shared with the partner flow, so the
// type matches what that hook returns. We treat it as the canonical
// "contestant pick" shape here too — the data is identical.
type ContestantPick = PartnerSearchResult;

function displayName(u: ContestantPick | null): string {
  if (!u) return '';
  const first = u.display_first_name || u.first_name || '';
  const last = u.display_last_name || u.last_name || '';
  const combined = [first, last].filter(Boolean).join(' ').trim();
  return combined || u.primary_email || 'Unknown';
}

export function AdminAddContestantDialog({
  eventId,
  open,
  onOpenChange,
}: AdminAddContestantDialogProps) {
  const { data: event } = useEvents(eventId);
  const isTeamRoping = useMemo(() => isTeamRopingEvent(event as any), [event]);
  const rodeoId = (event as any)?.rodeo_id as string | undefined;
  // Finals rodeos seat every contestant in every performance — preferences
  // are meaningless there and the backend nulls anything we send.
  const { data: rodeo } = useRodeos(rodeoId ?? '');
  const isFinalsRodeo = (rodeo as any)?.is_finals === true;

  const [contestantQuery, setContestantQuery] = useState('');
  const [contestant, setContestant] = useState<ContestantPick | null>(null);
  const [partner, setPartner] = useState<ContestantPick | null>(null);
  const [partnerQuery, setPartnerQuery] = useState('');
  const [position, setPosition] = useState<Position>('Header');
  const [partnerMode, setPartnerMode] = useState<PartnerMode>('with_partner');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('pay_later');
  const [firstPref, setFirstPref] = useState<string>(NONE_VALUE);
  const [secondPref, setSecondPref] = useState<string>(NONE_VALUE);
  const [perfOptions, setPerfOptions] = useState<PerfChoice[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reset all state whenever the dialog closes so reopening doesn't
  // surface a stale selection from a previous event.
  useEffect(() => {
    if (!open) {
      setContestantQuery('');
      setContestant(null);
      setPartner(null);
      setPartnerQuery('');
      setPosition('Header');
      setPartnerMode('with_partner');
      setPaymentMethod('pay_later');
      setFirstPref(NONE_VALUE);
      setSecondPref(NONE_VALUE);
      setError(null);
    }
  }, [open]);

  // When the admin switches to "Individual — no partner", drop any
  // partner pick they may have started entering so the submit payload
  // doesn't accidentally carry a stale partner_user_profile_id. Mode
  // is gated behind `isTeamRoping` in the UI but reset defensively for
  // any future code path that flips it.
  useEffect(() => {
    if (partnerMode === 'individual') {
      setPartner(null);
      setPartnerQuery('');
    }
  }, [partnerMode]);

  // Fetch performances for the rodeo so the admin can pick a 1st/2nd
  // preference. Mirrors EditPreferencesDialog so option values
  // round-trip identically with the stored preference strings (which
  // may carry a `[isoDatetime]` disambiguation suffix).
  useEffect(() => {
    if (!open || !rodeoId) {
      setPerfOptions(null);
      return;
    }
    let cancelled = false;
    httpClient
      .get<{
        data: Array<{
          id: string;
          performance_name: string | null;
          is_slack: boolean | null;
          start_datetime: string | null;
        }>;
      }>(`/performances?trig_rodeo_id=${rodeoId}&limit=50`)
      .then((res) => {
        if (cancelled) return;
        const rows = res?.data ?? [];
        const seen = new Set<string>();
        const opts: PerfChoice[] = [...rows]
          .filter((p) => !!p.performance_name)
          .sort((a, b) => {
            const aTime = a.start_datetime ? Date.parse(a.start_datetime) : 0;
            const bTime = b.start_datetime ? Date.parse(b.start_datetime) : 0;
            return aTime - bTime;
          })
          .reduce<PerfChoice[]>((acc, p) => {
            const name = p.performance_name as string;
            const isSlack = !!p.is_slack;
            const isoDate = p.start_datetime ?? '';
            const dedupeKey = `${isSlack ? 'slack' : 'perf'}::${name}::${isoDate}`;
            if (seen.has(dedupeKey)) return acc;
            seen.add(dedupeKey);
            const baseValue = isSlack ? 'Slack' : name;
            const value = isoDate ? `${baseValue} [${isoDate}]` : baseValue;
            // formatInTimezone() throws "Invalid time zone specified: ''"
            // when the zone param is an empty string — the function passes
            // it straight through to Intl.DateTimeFormat which rejects it.
            // Resolve the viewer's local zone explicitly so admins always
            // see a date/time on each option without depending on whether
            // a rodeo-level timezone has been set yet.
            const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const dateLabel = isoDate
              ? formatInTimezone(isoDate, localTz, {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })
              : '';
            const labelName =
              isSlack && name.toLowerCase() === 'slack' ? 'Slack' : name;
            const label = dateLabel ? `${labelName} — ${dateLabel}` : labelName;
            acc.push({ value, label, isSlack });
            return acc;
          }, []);
        setPerfOptions(opts);
      })
      .catch(() => {
        if (!cancelled) setPerfOptions(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, rodeoId]);

  const debouncedContestantQuery = useDebounce(contestantQuery, 400);
  const debouncedPartnerQuery = useDebounce(partnerQuery, 400);

  // For the contestant picker: include eventId so the dropdown flags
  // contestants already at the per-event cap (admin still sees them so
  // they can investigate, but the typed code from the backend will
  // surface a clear error if they try to add a duplicate).
  const { data: contestantResults = [], isLoading: contestantLoading } =
    usePartnerSearch(debouncedContestantQuery, { eventId });

  // For the partner picker (team-roping only): exclude the already-
  // picked contestant so they can't be their own partner. The standard
  // backend SELF_PARTNER_NOT_ALLOWED gate catches it server-side too.
  const { data: partnerResults = [], isLoading: partnerLoading } = usePartnerSearch(
    debouncedPartnerQuery,
    {
      eventId,
      excludeUserProfileId: contestant?.id ?? null,
    },
  );

  const createRegistration = useAdminCreateRegistration();

  // When the admin picks a contestant, check if they're already in a
  // buddy group for this rodeo. The backend admin-entry path auto-links
  // the new registration to that group; here we surface the fact so the
  // admin sees what's happening and lock the preference dropdowns to the
  // group's selections (group prefs win on the server too, see
  // registration.service.ts → resolvedFirstPref). Lookup is admin-only.
  const { data: contestantBuddyGroup } = useUserBuddyGroupForRodeo(
    rodeoId,
    contestant?.id ?? null,
  );

  // Pre-fill + lock 1st/2nd preference whenever the picked contestant
  // is in a buddy group for this rodeo. If the group has *locked* prefs,
  // pre-fill the dropdowns with those; if it hasn't set prefs yet, snap
  // back to "(No preference)" so the dropdowns visibly match what the
  // group has decided so far (i.e. nothing). Either way the dropdowns
  // are disabled — the group governs the entry, not the admin. Reset
  // back to "(No preference)" whenever the contestant is cleared or
  // swapped to someone NOT in a group, otherwise the previous pick's
  // group prefs would linger and get silently submitted for the new
  // contestant.
  useEffect(() => {
    if (contestant && contestantBuddyGroup?.preferences_locked_at) {
      setFirstPref(contestantBuddyGroup.preferences_first ?? NONE_VALUE);
      setSecondPref(contestantBuddyGroup.preferences_second ?? NONE_VALUE);
    } else {
      setFirstPref(NONE_VALUE);
      setSecondPref(NONE_VALUE);
    }
  }, [
    contestant?.id,
    contestantBuddyGroup?.id,
    contestantBuddyGroup?.preferences_locked_at,
    contestantBuddyGroup?.preferences_first,
    contestantBuddyGroup?.preferences_second,
  ]);

  // Group membership alone is enough to lock the dropdowns — even when
  // the group hasn't set prefs yet, the admin shouldn't be choosing
  // prefs on behalf of one member (those decisions belong to the group
  // owner via the Buddy Group setup page). The backend cascades any
  // future locked prefs onto this row automatically via
  // applyPreferencesToMemberRegistrations.
  // On a finals rodeo the backend drops buddy-group linkage entirely, so a
  // stale group the contestant happens to own must not gate this dialog.
  const inBuddyGroup = !isFinalsRodeo && !!contestantBuddyGroup;

  const partnerPosition: Position = position === 'Header' ? 'Heeler' : 'Header';

  // On team roping, a partner is only required when the admin is using
  // the "Pair with someone now" mode. The "Individual — no partner"
  // mode intentionally holds a partnerless seat that Draw Management
  // will pair up later, so it's submittable with just a contestant.
  const partnerlessTeamRopingEntry = isTeamRoping && partnerMode === 'individual';
  const canSubmit =
    !!contestant &&
    (!isTeamRoping || partnerlessTeamRopingEntry || !!partner) &&
    !createRegistration.isPending;

  const handleSubmit = async () => {
    if (!contestant) {
      setError('Pick a contestant to register.');
      return;
    }
    if (isTeamRoping && !partnerlessTeamRopingEntry && !partner) {
      setError('Team roping requires a partner — pick one from the search.');
      return;
    }
    setError(null);
    try {
      // NONE_VALUE → leave the column null so the placement engine
      // treats it as "no preference" (Pass 3 places into any named
      // perf with room). DRAW_OUT_VALUE on the 2nd pref is the legacy
      // sentinel meaning "rather not run than be placed in 2nd pref"
      // — backend categorises it via categorizePreference.
      //
      // When the contestant is in a buddy group, omit both fields
      // entirely — the group governs prefs even when it hasn't locked
      // them yet. The backend resolves prefs from the group via
      // resolvedFirstPref/resolvedSecondPref (or leaves them null if
      // the group hasn't set prefs, so the placement engine assigns
      // freely). Sending the dropdowns' values would either be a no-op
      // or risk a "user overrode group" error if prefs are rotated
      // server-side mid-submit.
      //
      // Strip the optional ` [<isoDatetime>]` disambiguation suffix
      // from the SelectItem value before submit. The suffix is used
      // internally to keep same-named perfs on different days apart in
      // the dropdown, but contestant self-registration stores clean
      // names ("Performance 1") — sending the suffixed value here
      // would make the admin-added row display "Performance 1 [...]"
      // in the draws list while regular rows show "Performance 1".
      // Stripping keeps the stored value consistent across entry paths.
      // Backend's categorizePreference already strips the suffix for
      // gating, so no behavior changes there.
      const stripDisambig = (v: string) =>
        v.replace(/\s*\[[^\]]*\]\s*$/, '').trim();
      const firstPrefValue =
        inBuddyGroup
          ? undefined
          : firstPref && firstPref !== NONE_VALUE
          ? stripDisambig(firstPref)
          : undefined;
      const secondPrefValue =
        inBuddyGroup
          ? undefined
          : secondPref && secondPref !== NONE_VALUE
          ? stripDisambig(secondPref)
          : undefined;
      await createRegistration.mutateAsync({
        eventId,
        target_user_profile_id: contestant.id,
        position: isTeamRoping ? position : undefined,
        // Only attach a partner when one was picked. In the
        // "Individual — no partner" path (`partnerlessTeamRopingEntry`)
        // we deliberately leave this undefined and instead flip
        // `as_individual_unmatched` so the backend persists the row as
        // `team_primary` / `partner_status='needs_partner'` for later
        // pairing on the Draw Management page.
        partner_user_profile_id:
          isTeamRoping && !partnerlessTeamRopingEntry ? partner!.id : undefined,
        as_individual_unmatched: partnerlessTeamRopingEntry || undefined,
        payment_method: paymentMethod,
        first_performance_preference: isFinalsRodeo ? undefined : firstPrefValue,
        second_performance_preference: isFinalsRodeo ? undefined : secondPrefValue,
      });
      onOpenChange(false);
    } catch (e: any) {
      // Surface backend-typed errors verbatim so the admin sees the
      // actionable copy (entry-closed, cap reached, partner already
      // entered, etc.) instead of a generic failure toast.
      const msg =
        e?.response?.data?.error ??
        e?.body?.error ??
        e?.message ??
        'Failed to register contestant.';
      setError(typeof msg === 'string' ? msg : 'Failed to register contestant.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Add Contestant Manually
          </DialogTitle>
          <DialogDescription>
            Enter a contestant on their behalf. All event rules (capacity,
            duplicates, fees) still apply. Payment defaults to “pay later” so
            the contestant can settle from their own account.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Contestant picker */}
          <div className="space-y-2">
            <Label>Contestant</Label>
            {contestant ? (
              <div className="flex items-center justify-between gap-2 rounded-lg border border-primary bg-primary/5 p-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {displayName(contestant)}
                  </p>
                  {contestant.primary_email && (
                    <p className="text-xs text-muted-foreground truncate">
                      {contestant.primary_email}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setContestant(null)}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Change
                </button>
              </div>
            ) : (
              <div className="relative space-y-1">
                <Input
                  placeholder="Search by name or email..."
                  value={contestantQuery}
                  onChange={(e) => setContestantQuery(e.target.value)}
                />
                {debouncedContestantQuery.length >= 2 && (
                  <div className="max-h-48 overflow-y-auto rounded-md border bg-popover shadow-sm">
                    {contestantLoading && (
                      <p className="px-3 py-2 text-xs text-muted-foreground">
                        Searching...
                      </p>
                    )}
                    {!contestantLoading && contestantResults.length === 0 && (
                      <p className="px-3 py-2 text-xs text-muted-foreground">
                        No users found
                      </p>
                    )}
                    {contestantResults.map((user) => {
                      const atMax = user.at_max_entries === true;
                      // Admin can still add a duplicate if they really
                      // need to, but we flag it so they know what they're
                      // doing. The backend's MAX_REGISTRATIONS_REACHED
                      // gate is the final word.
                      return (
                        <button
                          key={user.id}
                          type="button"
                          className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                          onClick={() => {
                            setContestant(user);
                            setContestantQuery('');
                          }}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">
                              {displayName(user)}
                            </p>
                            {user.primary_email && (
                              <p className="text-xs text-muted-foreground truncate">
                                {user.primary_email}
                              </p>
                            )}
                          </div>
                          {atMax && (
                            <span className="text-xs text-destructive shrink-0">
                              Already entered
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Team roping fields */}
          {isTeamRoping && (
            <>
              <div className="space-y-2">
                <Label>Position</Label>
                <Select
                  value={position}
                  onValueChange={(v) => setPosition(v as Position)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Position" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Header">Header</SelectItem>
                    <SelectItem value="Heeler">Heeler</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Partner mode: pair-now vs hold-partnerless. The hold
                  option intentionally lets an admin add a single
                  contestant who will be matched with another
                  unmatched individual later from Draw Management. */}
              <div className="space-y-2">
                <Label>Partner option</Label>
                <Select
                  value={partnerMode}
                  onValueChange={(v) => setPartnerMode(v as PartnerMode)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="with_partner">
                      Pair with a partner now
                    </SelectItem>
                    <SelectItem value="individual">
                      Individual — no partner (match later from Draw Management)
                    </SelectItem>
                  </SelectContent>
                </Select>
                {partnerlessTeamRopingEntry && (
                  <p className="text-xs text-muted-foreground">
                    This entry will be held without a partner. Pair it with
                    another unmatched contestant from the Draw Management
                    page when ready. The seat is excluded from the draw
                    until paired.
                  </p>
                )}
              </div>

              {!partnerlessTeamRopingEntry && (
              <div className="space-y-2">
                <Label>Partner ({partnerPosition})</Label>
                {partner ? (
                  <div className="flex items-center justify-between gap-2 rounded-lg border border-primary bg-primary/5 p-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {displayName(partner)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setPartner(null)}
                      className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <div className="relative space-y-1">
                    <Input
                      placeholder="Search partner by name or email..."
                      value={partnerQuery}
                      onChange={(e) => setPartnerQuery(e.target.value)}
                      disabled={!contestant}
                    />
                    {!contestant && (
                      <p className="text-xs text-muted-foreground">
                        Pick a contestant first.
                      </p>
                    )}
                    {contestant && debouncedPartnerQuery.length >= 2 && (
                      <div className="max-h-48 overflow-y-auto rounded-md border bg-popover shadow-sm">
                        {partnerLoading && (
                          <p className="px-3 py-2 text-xs text-muted-foreground">
                            Searching...
                          </p>
                        )}
                        {!partnerLoading && partnerResults.length === 0 && (
                          <p className="px-3 py-2 text-xs text-muted-foreground">
                            No users found
                          </p>
                        )}
                        {partnerResults.map((user) => {
                          const atMax = user.at_max_entries === true;
                          return (
                            <button
                              key={user.id}
                              type="button"
                              disabled={atMax}
                              className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm ${
                                atMax
                                  ? 'cursor-not-allowed opacity-60'
                                  : 'hover:bg-accent'
                              }`}
                              onClick={() => {
                                if (atMax) return;
                                setPartner(user);
                                setPartnerQuery('');
                              }}
                            >
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">
                                  {displayName(user)}
                                </p>
                              </div>
                              {atMax && (
                                <span className="text-xs text-destructive shrink-0">
                                  At entry limit
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}
            </>
          )}

          {/* Buddy group notice — surfaced when the picked contestant
              is already in an active buddy group for this rodeo. The
              admin-entry backend path auto-links the registration to
              that group (registration.service.ts → isAdminEntry), so
              we tell the admin what's about to happen and disable the
              preference dropdowns regardless of whether the group has
              set prefs yet: the group owns those decisions. */}
          {inBuddyGroup && contestantBuddyGroup && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">
              {displayName(contestant)} is in buddy group
              {' '}<span className="font-medium">{contestantBuddyGroup.group_name}</span>{' '}
              for this rodeo.{' '}
              {contestantBuddyGroup.preferences_locked_at
                ? 'Their 1st/2nd preference will use the group’s locked selections.'
                : 'The group has not set preferences yet — prefs are locked here and the placement engine will assign freely.'}
            </div>
          )}

          {/* Performance preferences — optional. Leave either at
              "(No preference)" to let the placement engine assign
              freely. Same option values + label format as the
              contestant-facing EditPreferencesDialog so a stored
              preference round-trips identically. The 2nd-pref dropdown
              also offers "Draw Out" as the legacy sentinel meaning
              "rather not run than be placed in 2nd pref".
              When the contestant belongs to a buddy group, both
              dropdowns are disabled regardless of whether the group
              has locked prefs — the group owns those decisions. */}
          {!isFinalsRodeo && perfOptions && perfOptions.length > 0 && (
            <>
              <div className="space-y-2">
                <Label>1st preference (optional)</Label>
                <Select
                  value={firstPref}
                  onValueChange={(v) => setFirstPref(v)}
                  disabled={inBuddyGroup}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_VALUE}>(No preference)</SelectItem>
                    {perfOptions.map((opt) => (
                      <SelectItem key={`p1-${opt.value}`} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>2nd preference (optional)</Label>
                <Select
                  value={secondPref}
                  onValueChange={(v) => setSecondPref(v)}
                  disabled={inBuddyGroup}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_VALUE}>(No preference)</SelectItem>
                    {perfOptions
                      // Don't let the admin pick the same exact perf
                      // as 1st AND 2nd — matches the contestant-side
                      // validation (validatePreferencePair).
                      .filter((opt) => opt.value !== firstPref)
                      .map((opt) => (
                        <SelectItem key={`p2-${opt.value}`} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    <SelectItem value={DRAW_OUT_VALUE}>Draw Out</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {/* Payment method — admins default to pay_later so the
              contestant can settle later from their own checkout flow.
              `cash` is also a defer-payment option; `stripe` lets the
              admin choose to require online payment up-front. */}
          <div className="space-y-2">
            <Label>Payment method</Label>
            <Select
              value={paymentMethod}
              onValueChange={(v) => setPaymentMethod(v as PaymentMethod)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pay_later">Pay later (contestant settles)</SelectItem>
                <SelectItem value="cash">Cash at event</SelectItem>
                <SelectItem value="stripe">Online payment required</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {error && (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createRegistration.isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {createRegistration.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Add Contestant
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
